import React, { useState, useEffect, useCallback } from 'react';
import { FaCalendar, FaChevronLeft, FaChevronRight, FaCircle, FaSpinner } from 'react-icons/fa';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import BottomNav from '../components/BottomNav';
import { useApp } from '../context/AppContext';
import { getAppointments } from '../services/api';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const TYPE_COLORS = {
  'Consultation': '#E8500A',
  'Follow-up': '#FF6B2C',
  'Emergency': '#e53935',
  'Telemedicine': '#1976d2',
  'Routine Check': '#2e7d32',
};

const Calendar = () => {
  const { user } = useApp();
  const role = user?.role;
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  const [current, setCurrent] = useState({ month: today.getMonth(), year: today.getFullYear() });
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [eventMap, setEventMap] = useState({});
  const [loading, setLoading] = useState(true);

  const prevMonth = () => setCurrent(p => p.month === 0 ? { month: 11, year: p.year - 1 } : { ...p, month: p.month - 1 });
  const nextMonth = () => setCurrent(p => p.month === 11 ? { month: 0, year: p.year + 1 } : { ...p, month: p.month + 1 });

  const loadAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAppointments();
      const appts = res.data?.data || [];
      // Group by date
      const map = {};
      appts.forEach(a => {
        const d = a.date?.split('T')[0] || a.date;
        if (!map[d]) map[d] = [];
        map[d].push({ time: a.time, patient: a.patientName, doctor: a.doctorName || a.doctor?.name, type: a.type, status: a.status });
      });
      setEventMap(map);
    } catch {
      setEventMap({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAppointments(); }, [loadAppointments]);

  const firstDay = new Date(current.year, current.month, 1).getDay();
  const daysInMonth = new Date(current.year, current.month + 1, 0).getDate();
  const formatDate = (d) => `${current.year}-${String(current.month + 1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
  const isToday = (d) => formatDate(d) === todayStr;
  const isSelected = (d) => formatDate(d) === selectedDate;
  const hasEvents = (d) => (eventMap[formatDate(d)]?.length || 0) > 0;

  const selectedEvents = eventMap[selectedDate] || [];

  const statusColor = (s) => {
    if (s === 'Completed') return '#2e7d32';
    if (s === 'Cancelled') return '#e53935';
    if (s === 'Pending') return '#f57c00';
    return '#1976d2';
  };

  return (
    <div className="page-wrapper">
      <Header title="Calendar" />
      <Sidebar />
      <main className="page-content">
        <div className="page-header">
          <div><h1><FaCalendar style={{ color: 'var(--primary)', marginRight: 8 }} />Calendar</h1><p>Your appointment schedule</p></div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-light)' }}>
            <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: 24, marginBottom: 8 }} /><br />Loading appointments…
          </div>
        ) : (
          <>
            <div className="cal-card card">
              <div className="cal-nav">
                <button className="cal-nav-btn" onClick={prevMonth}><FaChevronLeft /></button>
                <span className="cal-month-title">{MONTHS[current.month]} {current.year}</span>
                <button className="cal-nav-btn" onClick={nextMonth}><FaChevronRight /></button>
              </div>

              <div className="cal-grid-header">
                {DAYS.map(d => <div key={d} className="cal-day-name">{d}</div>)}
              </div>

              <div className="cal-grid">
                {Array(firstDay).fill(null).map((_, i) => <div key={`e${i}`} className="cal-cell empty" />)}
                {Array(daysInMonth).fill(null).map((_, i) => {
                  const d = i + 1;
                  return (
                    <div
                      key={d}
                      className={`cal-cell ${isToday(d) ? 'today' : ''} ${isSelected(d) ? 'selected' : ''}`}
                      onClick={() => setSelectedDate(formatDate(d))}
                    >
                      <span className="cal-num">{d}</span>
                      {hasEvents(d) && <FaCircle className="cal-event-dot" />}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="cal-events">
              <div className="section-title">
                {selectedDate
                  ? new Date(selectedDate + 'T00:00').toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
                  : 'Appointments'}
                {selectedEvents.length > 0 && (
                  <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--text-light)', fontWeight: 500 }}>
                    ({selectedEvents.length} appointment{selectedEvents.length !== 1 ? 's' : ''})
                  </span>
                )}
              </div>
              {selectedEvents.length === 0 ? (
                <div className="empty-state" style={{ padding: '30px 0' }}>
                  <FaCalendar className="empty-state-icon" />
                  <p>No appointments on this day</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {selectedEvents
                    .slice()
                    .sort((a, b) => a.time.localeCompare(b.time))
                    .map((ev, i) => (
                    <div key={i} className="card cal-event-card">
                      <div className="cal-event-time">{ev.time}</div>
                      <div className="cal-event-info">
                        {role === 'Patient' ? (
                          <div className="cal-event-patient">Dr. {ev.doctor || 'Unknown'}</div>
                        ) : role === 'Admin' ? (
                          <div className="cal-event-patient">
                            {ev.patient || 'Unknown Patient'} 
                            <span style={{fontSize: 11, color: 'var(--text-light)', fontWeight: 'normal', marginLeft: 4}}>
                              (with Dr. {ev.doctor || 'Unknown'})
                            </span>
                          </div>
                        ) : (
                          <div className="cal-event-patient">{ev.patient || 'Unknown Patient'}</div>
                        )}
                        <div className="cal-event-type" style={{ color: TYPE_COLORS[ev.type] || 'var(--text-light)' }}>
                          {ev.type}
                        </div>
                      </div>
                      <span className="status-badge" style={{ background: statusColor(ev.status) + '20', color: statusColor(ev.status), border: `1px solid ${statusColor(ev.status)}40` }}>
                        {ev.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </main>
      <BottomNav />

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .cal-card { padding: 16px; margin-bottom: 16px; }
        .cal-nav { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
        .cal-nav-btn { width: 34px; height: 34px; border-radius: 50%; background: var(--primary-bg); border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; color: var(--primary); font-size: 14px; }
        .cal-nav-btn:hover { background: var(--primary); color: white; }
        .cal-month-title { font-size: 16px; font-weight: 700; }
        .cal-grid-header { display: grid; grid-template-columns: repeat(7,1fr); margin-bottom: 4px; }
        .cal-day-name { text-align: center; font-size: 11px; font-weight: 700; color: var(--text-light); padding: 4px 0; text-transform: uppercase; }
        .cal-grid { display: grid; grid-template-columns: repeat(7,1fr); gap: 2px; }
        .cal-cell { display: flex; flex-direction: column; align-items: center; padding: 8px 4px; border-radius: 10px; cursor: pointer; transition: all 0.15s; min-height: 42px; position: relative; }
        .cal-cell:hover { background: var(--primary-bg); }
        .cal-cell.today .cal-num { background: var(--primary); color: white; border-radius: 50%; width: 28px; height: 28px; display: flex; align-items: center; justify-content: center; }
        .cal-cell.selected { background: var(--primary-pale, #fff3ee); }
        .cal-cell.empty { pointer-events: none; }
        .cal-num { font-size: 13px; font-weight: 600; color: var(--text-dark); }
        .cal-event-dot { font-size: 6px; color: var(--primary); position: absolute; bottom: 4px; }
        .cal-events { margin-top: 4px; }
        .cal-event-card { display: flex; align-items: center; gap: 12px; padding: 12px 14px; }
        .cal-event-time { font-size: 13px; font-weight: 700; color: var(--primary); min-width: 50px; }
        .cal-event-info { flex: 1; }
        .cal-event-patient { font-size: 14px; font-weight: 700; }
        .cal-event-type { font-size: 12px; font-weight: 600; margin-top: 2px; }
        .status-badge { padding: 3px 8px; border-radius: 20px; font-size: 11px; font-weight: 700; }
      `}</style>
    </div>
  );
};

export default Calendar;
